import {PaymentManager, PaymentManagerOptions} from './PaymentManager';
import {DEFAULT_CUSTOM_PAYMENT_METHODS, PaymentConfig} from '../types';
import {ConfigValidator} from './ConfigValidator';

export const BASE_URLS = {
    production: 'https://payment.elsanow.io',
    staging: 'https://payment-stag.elsanow.io',
    development: 'http://localhost:8001', // Or a development-specific URL https://payment-stag.elsanow.io
};

/**
 * Common base functionality for all payment SDK implementations
 */
export class PaymentSDKBase {
    protected paymentManager: PaymentManager;
    protected config: PaymentConfig;

    constructor(config: PaymentConfig, options: Partial<PaymentManagerOptions> = {}) {
        // Validate configuration using common validator
        ConfigValidator.validate(config);

        // Add baseUrl to config based on environment
        this.config = {
            ...config,
            baseUrl: BASE_URLS[config.environment],
            customPaymentMethods: config.customPaymentMethods ?? DEFAULT_CUSTOM_PAYMENT_METHODS,
            defaultPaymentMethod: config.defaultPaymentMethod ?? 'stripe_js'
        };

        // Create payment manager (will handle payment method filtering internally)
        const defaultOptions: PaymentManagerOptions = {
            config: this.config,
            showToasts: true,
            autoInjectStyles: true,
            ...options
        };

        this.paymentManager = new PaymentManager(defaultOptions);
    }

    /**
     * Initialize payment with common error handling
     */
    protected async initializePayment(): Promise<void> {
        try {
            await this.paymentManager.initialize();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to initialize payment';
            console.error('Payment initialization error:', error);
            throw new Error(errorMessage);
        }
    }

    /**
     * Confirm payment with common error handling
     */
    protected async confirmPayment(): Promise<{ success: boolean; paymentIntent?: any; error?: string }> {
        try {
            return await this.paymentManager.confirmPayment();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Payment confirmation failed';
            console.error('Payment confirmation error:', error);
            return {success: false, error: errorMessage};
        }
    }

    /**
     * Cleanup resources
     */
    protected destroy(): void {
        if (this.paymentManager) {
            this.paymentManager.destroy();
        }
    }
}

/**
 * Common CSS styles and animations
 */
export const COMMON_STYLES = {
    animation: `
        @keyframes elsa-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
    `,

    submitButton: {
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '15px 20px',
        fontSize: '16px',
        fontWeight: '500',
        width: '100%',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
    },

    submitButtonDisabled: {
        opacity: '0.6',
        cursor: 'not-allowed',
    },

    errorContainer: {
        color: '#dc3545',
        padding: '15px',
        backgroundColor: '#f8d7da',
        border: '1px solid #f5c6cb',
        borderRadius: '8px',
        textAlign: 'center' as const,
    },

    elementContainer: {
        marginBottom: '20px',
    }
};

/**
 * Common element IDs used across all implementations
 */
export const ELEMENT_IDS = {
    paymentElement: 'payment-element',
    submitButton: 'submit-button',
    paymentForm: 'payment-form',
    messageContainer: 'payment-message',
    // Custom payment method elements
    customPaymentMethods: 'custom-payment-methods',
    stripeElementContainer: 'stripe-element-container',
    paymentMethodRadio: (id: string) => `payment-method-${id}`,
    // Promotion code elements
    promotionContainer: 'elsa-promotion-container',
    promotionInput: 'elsa-promotion-code-input',
    promotionApplyBtn: 'elsa-promotion-apply-btn',
    promotionRemoveBtn: 'elsa-promotion-remove-btn',
    taxCheckbox: 'elsa-tax-checkbox',
    taxFormContainer: 'elsa-tax-form',
    taxNumber: 'elsa-tax-number',
    taxCompany: 'elsa-tax-company',
    taxBuyerName: 'elsa-tax-buyer-name',
    taxAddress: 'elsa-tax-address',
    taxEmail: 'elsa-tax-email',
    taxError: 'elsa-tax-error',
} as const;
